import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Container,
  IconButton,
  Collapse,
  Link,
  Tooltip,
  Alert,
  Pagination,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import axios from 'axios';
import GoldIngotLoader from './GoldIngotLoader';

// Market configurations
const MARKETS = [
  { code: 'US', name: 'US', domain: 'amazon.com', flag: '🇺🇸' },
  { code: 'UK', name: 'UK', domain: 'amazon.co.uk', flag: '🇬🇧' },
  { code: 'JP', name: 'JP', domain: 'amazon.co.jp', flag: '🇯🇵' },
  { code: 'CA', name: 'CA', domain: 'amazon.ca', flag: '🇨🇦' },
];

// Get color based on search frequency rank
const getRankColor = (rank) => {
  if (rank <= 100) return '#4caf50';      // Green - Top 100
  if (rank <= 1000) return '#2196f3';     // Blue - Top 1000
  if (rank <= 10000) return '#ff9800';    // Orange - Top 10000
  return '#9e9e9e';                        // Gray
};

// Get position chip with medal colors
const getPositionChip = (position) => {
  const colors = {
    1: { bg: '#ffd700', text: '#000' },  // Gold
    2: { bg: '#c0c0c0', text: '#000' },  // Silver
    3: { bg: '#cd7f32', text: '#fff' },  // Bronze
  };
  const color = colors[position] || { bg: '#e0e0e0', text: '#000' };
  return (
    <Chip
      label={`#${position}`}
      size="small"
      sx={{
        backgroundColor: color.bg,
        color: color.text,
        fontWeight: 'bold',
        fontSize: '0.7rem',
        height: 20,
      }}
    />
  );
};

// Get Amazon domain based on market code
const getAmazonDomain = (marketCode) => {
  const m = MARKETS.find(m => m.code === marketCode);
  return m?.domain || 'amazon.com';
};

// Format sales number
const formatSales = (sales) => {
  if (!sales && sales !== 0) return null;
  if (sales >= 1000) {
    return `${(sales / 1000).toFixed(1)}K`;
  }
  return sales.toLocaleString();
};

// Top 3 Product Display Component
const Top3ProductsCell = ({ term, targetAsin, marketCode = 'US' }) => {
  const products = [
    { pos: 1, asin: term.product1_asin, title: term.product1_title, click: term.product1_click_share, conv: term.product1_conversion_share, sales: term.product1_monthly_sales },
    { pos: 2, asin: term.product2_asin, title: term.product2_title, click: term.product2_click_share, conv: term.product2_conversion_share, sales: term.product2_monthly_sales },
    { pos: 3, asin: term.product3_asin, title: term.product3_title, click: term.product3_click_share, conv: term.product3_conversion_share, sales: term.product3_monthly_sales },
  ].filter(p => p.asin);

  if (products.length === 0) return <Typography variant="body2" sx={{ color: '#999' }}>-</Typography>;

  const domain = getAmazonDomain(marketCode);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {products.map((p) => {
        const isTarget = p.asin === targetAsin;
        return (
          <Box
            key={p.pos}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              p: 0.5,
              borderRadius: 1,
              backgroundColor: isTarget ? '#fff3e0' : 'transparent',
              border: isTarget ? '1px solid #ff9800' : '1px solid transparent',
            }}
          >
            {getPositionChip(p.pos)}
            <Chip
              label={p.asin}
              size="small"
              component="a"
              href={`https://${domain}/dp/${p.asin}`}
              target="_blank"
              clickable
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.65rem',
                height: 18,
                backgroundColor: isTarget ? '#ff9800' : '#f5f5f5',
                color: isTarget ? 'white' : '#333',
                '&:hover': { backgroundColor: isTarget ? '#e65100' : '#e0e0e0' },
              }}
            />
            <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#2196f3' }}>
              {parseFloat(p.click || 0).toFixed(1)}%
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#4caf50' }}>
              {parseFloat(p.conv || 0).toFixed(1)}%
            </Typography>
            {/* Monthly Sales */}
            <Tooltip title={p.sales ? `Monthly Sales: ${p.sales.toLocaleString()}` : 'No sales data'}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.65rem',
                  color: p.sales ? '#e65100' : '#bbb',
                  fontWeight: p.sales ? 'bold' : 'normal',
                  ml: 0.5,
                }}
              >
                {p.sales ? `(${formatSales(p.sales)}/mo)` : '(-)'}
              </Typography>
            </Tooltip>
          </Box>
        );
      })}
    </Box>
  );
};

// Single ASIN Keywords Card
const AsinKeywordsCard = ({ asin, productName, terms, expanded, onToggle, marketCode = 'US' }) => {
  const domain = getAmazonDomain(marketCode);

  return (
    <Card
      sx={{
        mb: 2,
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.15)' },
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        {/* Header Row */}
        <Box
          onClick={onToggle}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={asin}
              size="small"
              component="a"
              href={`https://${domain}/dp/${asin}`}
              target="_blank"
              clickable
              sx={{
                fontFamily: 'monospace',
                fontWeight: 'bold',
                backgroundColor: '#ff9800',
                color: 'white',
              }}
            />
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {productName}
            </Typography>
            <Chip
              label={`${terms.length} keywords`}
              size="small"
              sx={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}
            />
          </Box>
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {/* Expanded Content */}
        <Collapse in={expanded}>
          <TableContainer sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: 40 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Search Term</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Search Rank</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Top Clicked Brands</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Top Categories</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Top 3 Products (Click% / Conv% / Sales)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {terms.map((term, idx) => (
                  <TableRow
                    key={idx}
                    sx={{
                      '&:hover': { backgroundColor: '#fafafa' },
                      '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                      verticalAlign: 'top',
                    }}
                  >
                    <TableCell sx={{ color: '#666', width: 40 }}>{idx + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SearchIcon sx={{ fontSize: 16, color: '#bbb' }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {term.search_term}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`#${term.search_frequency_rank?.toLocaleString()}`}
                        size="small"
                        sx={{
                          backgroundColor: getRankColor(term.search_frequency_rank),
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.75rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#666' }}>
                        {term.top_clicked_brands || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#666' }}>
                        {term.top_clicked_categories || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Top3ProductsCell term={term} targetAsin={asin} marketCode={marketCode} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
      </CardContent>
    </Card>
  );
};

// Main Page Component
const SearchTermsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expandedAsins, setExpandedAsins] = useState({});
  const [market, setMarket] = useState('US');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Fetch data with pagination
  const fetchData = async (marketCode = market, pageNum = page, size = pageSize) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/search-terms/all-rankings', {
        params: { market: marketCode, limit: 10, page: pageNum, pageSize: size },
      });
      if (response.data.success) {
        setData(response.data);
        // Expand first 3 by default
        const expanded = {};
        response.data.configs?.slice(0, 3).forEach(c => {
          expanded[c.asin] = true;
        });
        setExpandedAsins(expanded);
      } else {
        setError(response.data.error || 'Failed to load data');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarketChange = (event, newMarket) => {
    if (newMarket) {
      setMarket(newMarket);
      setPage(1); // Reset to page 1 when market changes
      fetchData(newMarket, 1, pageSize);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    fetchData(market, newPage, pageSize);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (event) => {
    const newSize = event.target.value;
    setPageSize(newSize);
    setPage(1);
    fetchData(market, 1, newSize);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleExpand = (asin) => {
    setExpandedAsins(prev => ({
      ...prev,
      [asin]: !prev[asin],
    }));
  };

  const expandAll = () => {
    const expanded = {};
    data?.configs?.forEach(c => {
      expanded[c.asin] = true;
    });
    setExpandedAsins(expanded);
  };

  const collapseAll = () => {
    setExpandedAsins({});
  };

  if (loading) {
    return (
      <GoldIngotLoader minHeight={400} />
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TrendingUpIcon sx={{ fontSize: 32, color: '#ff9800' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Search Terms Rankings
            </Typography>
            {data?.latestWeek && (
              <Chip
                label={`Week: ${data.latestWeek}`}
                size="small"
                sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Market Selector */}
            <ToggleButtonGroup
              value={market}
              exclusive
              onChange={handleMarketChange}
              size="small"
            >
              {MARKETS.map((m) => (
                <ToggleButton
                  key={m.code}
                  value={m.code}
                  sx={{
                    px: 2,
                    '&.Mui-selected': {
                      backgroundColor: '#ff9800',
                      color: 'white',
                      '&:hover': { backgroundColor: '#e65100' },
                    },
                  }}
                >
                  <span style={{ marginRight: 4 }}>{m.flag}</span>
                  {m.code}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <Tooltip title="Expand All">
              <IconButton onClick={expandAll} size="small">
                <ExpandMoreIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Collapse All">
              <IconButton onClick={collapseAll} size="small">
                <ExpandLessIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton onClick={() => fetchData(market)} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Statistics */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#fff3e0' }}>
              <Typography variant="h4" sx={{ color: '#e65100', fontWeight: 'bold' }}>
                {data?.pagination?.total || data?.configs?.length || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total ASINs
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#e3f2fd' }}>
              <Typography variant="h4" sx={{ color: '#1565c0', fontWeight: 'bold' }}>
                {data?.configs?.length || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Showing on Page
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#e8f5e9' }}>
              <Typography variant="h4" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                {data?.market || 'US'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Market
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#fce4ec' }}>
              <Typography variant="h4" sx={{ color: '#c2185b', fontWeight: 'bold' }}>
                {data?.pagination?.page || 1}/{data?.pagination?.totalPages || 1}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Page
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* ASIN Keywords Card List */}
      {data?.configs?.map((config) => (
        <AsinKeywordsCard
          key={config.asin}
          asin={config.asin}
          productName={config.product_name}
          terms={data.rankings[config.asin]?.terms || []}
          expanded={expandedAsins[config.asin] || false}
          onToggle={() => toggleExpand(config.asin)}
          marketCode={market}
        />
      ))}

      {(!data?.configs || data.configs.length === 0) && (
        <Alert severity="info">
          No monitored ASINs found for this market.
        </Alert>
      )}

      {/* Pagination */}
      {data?.pagination?.totalPages > 1 && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 3,
          mt: 4,
          mb: 4,
          p: 2,
          backgroundColor: '#f5f5f5',
          borderRadius: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">Per page:</Typography>
            <FormControl size="small">
              <Select
                value={pageSize}
                onChange={handlePageSizeChange}
                sx={{ minWidth: 80 }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Pagination
            count={data.pagination.totalPages}
            page={data.pagination.page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            sx={{
              '& .MuiPaginationItem-root': {
                '&.Mui-selected': {
                  backgroundColor: '#ff9800',
                  color: 'white',
                  '&:hover': { backgroundColor: '#e65100' },
                },
              },
            }}
          />
          <Typography variant="body2" color="textSecondary">
            {((data.pagination.page - 1) * pageSize) + 1} - {Math.min(data.pagination.page * pageSize, data.pagination.total)} of {data.pagination.total}
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default SearchTermsPage;
